import { useState } from "react";
import { Modal, Input, Select, Checkbox, Button, Upload, Avatar, notification } from "antd";
import {
  CalendarOutlined,
  CarOutlined,
  EnvironmentOutlined,
  MailOutlined,
  TeamOutlined,
  FileTextOutlined,
  TagsOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import type { Employee, EmployeeTag } from "./types";
import SignatureSection from "./SignatureSection";

const { TextArea } = Input;

interface AddEmployeeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: (employee: Employee) => void;
  allTags?: EmployeeTag[];
}

function AddEmployeeModal({ open, onOpenChange, onAdded, allTags = [] }: AddEmployeeModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [profileImg, setProfileImg] = useState("");
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    birthday: "",
    bloodType: "",
    idNumber: "",
    hasMotorcycleLicense: false,
    hasCarLicense: false,
    city: "",
    district: "",
    postalCode: "",
    address: "",
    emergencyContact: "",
    emergencyRelation: "",
    emergencyPhone: "",
    notes: "",
  });

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBeforeUpload = async (file: File) => {
    if (uploadState) return false;
    setUploadState(true);
    try {
      const { blobSasUrl, blobUrl } = await orpcClient.storage.createBlobSasUrl({
        fileName: file.name,
        contentType: file.type,
      });
      await fetch(blobSasUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type, "x-ms-blob-type": "BlockBlob" },
      });
      setProfileImg(URL.createObjectURL(file));
      setProfileUrl(blobUrl);
    } catch (error) {
      console.error("upload error", error);
    } finally {
      setUploadState(false);
    }
    return false;
  };

  const handleClose = () => {
    setProfileImg("");
    setProfileUrl(null);
    setSelectedTagIds([]);
    setFormData({
      email: "",
      name: "",
      birthday: "",
      bloodType: "",
      idNumber: "",
      hasMotorcycleLicense: false,
      hasCarLicense: false,
      city: "",
      district: "",
      postalCode: "",
      address: "",
      emergencyContact: "",
      emergencyRelation: "",
      emergencyPhone: "",
      notes: "",
    });
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!formData.email) {
      api.error({ message: t("employee.add.emailRequired"), placement: "topRight" });
      return;
    }
    setLoading(true);
    try {
      const employee = await orpcClient.employee.addMember({ email: formData.email });
      if (!employee) {
        api.error({ message: t("employee.add.addFailed"), placement: "topRight" });
        return;
      }
      const hasAddress = formData.city || formData.district || formData.postalCode || formData.address;
      const updateData: Parameters<typeof orpcClient.employee.update>[0][0] = {
        id: employee.id,
        name: formData.name || undefined,
        bloodType: formData.bloodType || undefined,
        nationalIdNo: formData.idNumber || undefined,
        motorbikeLicense: formData.hasMotorcycleLicense,
        carLicense: formData.hasCarLicense,
        profile: profileUrl || undefined,
        address: hasAddress
          ? {
              city: formData.city,
              district: formData.district,
              postalCode: formData.postalCode ? Number(formData.postalCode) : 0,
              address: formData.address,
            }
          : undefined,
        emergencyContact: formData.emergencyContact || undefined,
        emergencyContactRelationship: formData.emergencyRelation || undefined,
        emergencyContactPhoneNumber: formData.emergencyPhone || undefined,
        note: formData.notes || undefined,
      };
      if (formData.birthday) {
        updateData.birthday = new Date(formData.birthday);
      }
      const updated = await single(orpcClient.employee.update([updateData]));
      if (!updated) {
        api.error({ message: t("employee.add.addFailed"), placement: "topRight" });
        return;
      }
      await Promise.all(
        selectedTagIds.map((tagId) =>
          orpcClient.employeeTag.addEmployee({
            employeeTagId: tagId,
            employeeId: [updated.id],
          }),
        ),
      );
      const appliedTags = allTags.filter((t) => selectedTagIds.includes(t.id));
      onAdded({
        id: updated.id,
        name: updated.name,
        email: updated.user.email,
        avatar: updated.profile ?? undefined,
        birthday: updated.birthday ? new Date(updated.birthday).toISOString().split("T")[0] : undefined,
        bloodType: updated.bloodType ?? undefined,
        idNumber: updated.nationalIdNo ?? undefined,
        hasMotorcycleLicense: updated.motorbikeLicense ?? undefined,
        hasCarLicense: updated.carLicense ?? undefined,
        city: updated.address?.city ?? undefined,
        district: updated.address?.district ?? undefined,
        postalCode: updated.address?.postalCode?.toString() ?? undefined,
        address: updated.address?.address ?? undefined,
        emergencyContact: updated.emergencyContact ?? undefined,
        emergencyRelation: updated.emergencyContactRelationship ?? undefined,
        emergencyPhone: updated.emergencyContactPhoneNumber ?? undefined,
        notes: updated.note ?? undefined,
        permissions: [],
        tags: appliedTags,
      });
      handleClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : t("employee.add.addFailed");
      api.error({ message: msg, placement: "topRight" });
    } finally {
      setLoading(false);
    }
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 500,
    color: "#999",
    marginBottom: 12,
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 4,
  };

  return (
    <>
    {contextHolder}
    <Modal
      open={open}
      onCancel={handleClose}
      title={t("employee.add.title")}
      width={700}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t("employee.add.cancel")}
        </Button>,
        <Button key="save" type="primary" loading={loading} onClick={handleSubmit}>
          {t("employee.add.submit")}
        </Button>,
      ]}
      styles={{
        body: { maxHeight: "60vh", overflowY: "auto", paddingRight: 8 },
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <Upload
          name="avatar"
          listType="picture-card"
          showUploadList={false}
          beforeUpload={handleBeforeUpload}
        >
          {profileImg ? (
            <img src={profileImg} alt="avatar" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: "50%" }} />
          ) : (
            <Avatar size={80} icon={<UserOutlined />} />
          )}
        </Upload>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          <div style={sectionTitleStyle}>
            <MailOutlined />
            <span>{t("employee.add.accountInfo")}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>
                {t("employee.email")} <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <Input
                type="email"
                placeholder={t("employee.add.emailPlaceholder")}
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.name")}</label>
              <Input
                prefix={<UserOutlined style={{ color: "#999" }} />}
                placeholder={t("employee.add.namePlaceholder")}
                value={formData.name}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <CalendarOutlined />
            <span>{t("employee.edit.basicData")}</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={labelStyle}>{t("employee.edit.birthday")}</label>
              <Input
                type="date"
                value={formData.birthday}
                onChange={(e) => updateField("birthday", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.bloodType")}</label>
              <Select
                value={formData.bloodType || undefined}
                onChange={(value) => updateField("bloodType", value)}
                placeholder={t("employee.edit.bloodTypePlaceholder")}
                style={{ width: "100%" }}
                options={[
                  { value: "A", label: t("employee.edit.bloodTypeA") },
                  { value: "B", label: t("employee.edit.bloodTypeB") },
                  { value: "AB", label: t("employee.edit.bloodTypeAB") },
                  { value: "O", label: t("employee.edit.bloodTypeO") },
                ]}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.idNumber")}</label>
              <Input
                value={formData.idNumber}
                onChange={(e) => updateField("idNumber", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <CarOutlined />
            <span>{t("employee.edit.licenses")}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
            <Checkbox
              checked={formData.hasMotorcycleLicense}
              onChange={(e) => updateField("hasMotorcycleLicense", e.target.checked)}
            >
              {t("employee.edit.motorcycle")}
            </Checkbox>
            <Checkbox
              checked={formData.hasCarLicense}
              onChange={(e) => updateField("hasCarLicense", e.target.checked)}
            >
              {t("employee.edit.car")}
            </Checkbox>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <EnvironmentOutlined />
            <span>{t("employee.edit.contactAddress")}</span>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label style={labelStyle}>{t("employee.edit.city")}</label>
              <Input
                value={formData.city}
                onChange={(e) => updateField("city", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.district")}</label>
              <Input
                value={formData.district}
                onChange={(e) => updateField("district", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.postalCode")}</label>
              <Input
                value={formData.postalCode}
                onChange={(e) => updateField("postalCode", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.address")}</label>
              <Input
                value={formData.address}
                onChange={(e) => updateField("address", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <TeamOutlined />
            <span>{t("employee.edit.emergencyContact")}</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={labelStyle}>{t("employee.edit.emergencyContact")}</label>
              <Input
                value={formData.emergencyContact}
                onChange={(e) => updateField("emergencyContact", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.relationship")}</label>
              <Input
                value={formData.emergencyRelation}
                onChange={(e) => updateField("emergencyRelation", e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("employee.edit.emergencyPhone")}</label>
              <Input
                value={formData.emergencyPhone}
                onChange={(e) => updateField("emergencyPhone", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <div style={sectionTitleStyle}>
            <FileTextOutlined />
            <span>{t("employee.edit.notes")}</span>
          </div>
          <TextArea
            placeholder={t("employee.edit.notesPlaceholder")}
            value={formData.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            rows={3}
          />
        </div>

        <SignatureSection sectionTitleStyle={sectionTitleStyle} />

        {allTags.length > 0 && (
          <div>
            <div style={sectionTitleStyle}>
              <TagsOutlined />
              <span>{t("employee.edit.tags")}</span>
            </div>
            <Select
              mode="multiple"
              placeholder={t("employee.add.selectTags")}
              style={{ width: "100%" }}
              value={selectedTagIds}
              onChange={(val) => setSelectedTagIds(val)}
              options={allTags.map((t) => ({ label: t.name, value: t.id }))}
            />
          </div>
        )}
      </div>
    </Modal>
    </>
  );
}

export default AddEmployeeModal;
